<template>
  <AuthLayout>
    <RouterLink :to="{ name: 'login' }" class="tw:text-13 tw:font-medium tw:text-gray-600!">
      {{ $t('auth.back_to_sign_in') }}
    </RouterLink>

    <h1 class="tw:m-0! tw:text-30! tw:font-bold! tw:tracking-[-0.02em]! tw:text-gray-900!">
      {{ $t('reset_password.title', { app: brand.shortName }) }}
    </h1>

    <form class="tw:flex tw:flex-col tw:gap-6" novalidate @submit.prevent="resetPassword">
      <LeadInput
        v-model="email"
        type="email"
        autocomplete="username"
        placeholder="your@email.com"
        data-testid="forgot-email"
        :label="$t('email_address')"
        :leading-icon="IconMail"
        :errors="errors" />
      <LeadButton type="submit" block :loading="isSubmitting" :disabled="!isValidInput" data-testid="forgot-submit">
        {{ $t('reset_password.reset_button') }}
      </LeadButton>
    </form>
  </AuthLayout>
</template>

<script setup lang="ts">
import { brand } from '@/config/brands';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { IconMail } from '@tabler/icons-vue';
import { useToast } from 'vue-toastification';

import { requestUserResetPassword } from '@/api/auth';
import type { ClientError } from '@/api/client';
import { useUserStore } from '@/stores/user';
import translator from '@/locale/translator';
import AuthLayout from '@/components/auth/AuthLayout.vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadInput from '@/components/lead-design/LeadInput.vue';

const router = useRouter();
const userStore = useUserStore();
const toast = useToast();

/* ---------------------------------- STATE ---------------------------------- */
const email = ref('');
const errors = ref<string[]>([]);
const isSubmitting = ref(false);

/* -------------------------------- COMPUTED --------------------------------- */
const isValidInput = computed(() => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.value);
});

/* --------------------------------- METHODS --------------------------------- */
function resetPassword() {
  if (!isValidInput.value || isSubmitting.value) return;

  isSubmitting.value = true;
  requestUserResetPassword(email.value)
    .then(() => {
      userStore.fetchCurrent().then(async () => {
        router.push(userStore.isLoggedIn ? userStore.authenticatedLandingRoute() : { name: 'login' });
        toast.success(translator.get('reset_password.email_sent'));
      });
    })
    .catch((error: ClientError) => {
      errors.value = error.responseJson.errors ?? [];
    })
    .finally(() => {
      isSubmitting.value = false;
    });
}
</script>
